import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { MapPin, Phone, Mail, MessageCircle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, SITE } from "@/lib/api";
import { PRODUCTS } from "@/lib/content";

function FarmerForm() {
  const [form, setForm] = useState({ name: "", phone: "", village: "", taluka: "", district: "", land_size: "", crop_type: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.village) {
      toast.error("Please fill name, phone and village");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/farmers/register", form);
      toast.success("Registration submitted! Our team will reach out to you soon.");
      setForm({ name: "", phone: "", village: "", taluka: "", district: "", land_size: "", crop_type: "", notes: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="farmer-registration-form">
      <div>
        <Label htmlFor="f-name" className="mb-1.5 block">Full Name *</Label>
        <Input id="f-name" data-testid="farmer-name" value={form.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Your full name" />
      </div>
      <div>
        <Label htmlFor="f-phone" className="mb-1.5 block">Phone Number *</Label>
        <Input id="f-phone" data-testid="farmer-phone" value={form.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="10-digit mobile" />
      </div>
      <div>
        <Label htmlFor="f-village" className="mb-1.5 block">Village *</Label>
        <Input id="f-village" data-testid="farmer-village" value={form.village} onChange={(e) => onChange("village", e.target.value)} placeholder="Your village" />
      </div>
      <div>
        <Label htmlFor="f-taluka" className="mb-1.5 block">Taluka</Label>
        <Input id="f-taluka" data-testid="farmer-taluka" value={form.taluka} onChange={(e) => onChange("taluka", e.target.value)} placeholder="Taluka" />
      </div>
      <div>
        <Label htmlFor="f-district" className="mb-1.5 block">District</Label>
        <Input id="f-district" data-testid="farmer-district" value={form.district} onChange={(e) => onChange("district", e.target.value)} placeholder="District" />
      </div>
      <div>
        <Label htmlFor="f-land" className="mb-1.5 block">Land Size (acres)</Label>
        <Input id="f-land" data-testid="farmer-land" value={form.land_size} onChange={(e) => onChange("land_size", e.target.value)} placeholder="e.g. 2.5" />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="f-crop" className="mb-1.5 block">Main Crop / Livestock</Label>
        <Input id="f-crop" data-testid="farmer-crop" value={form.crop_type} onChange={(e) => onChange("crop_type", e.target.value)} placeholder="e.g. Sugarcane, Dairy" />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="f-notes" className="mb-1.5 block">Additional Notes</Label>
        <Textarea id="f-notes" data-testid="farmer-notes" rows={3} value={form.notes} onChange={(e) => onChange("notes", e.target.value)} placeholder="Anything we should know" />
      </div>
      <div className="sm:col-span-2">
        <Button type="submit" disabled={submitting} className="rounded-full bg-primary hover:bg-primary/90 w-full sm:w-auto px-8" data-testid="farmer-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Submit Registration
        </Button>
      </div>
    </form>
  );
}

function EnquiryForm({ initialProduct }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", product: initialProduct || "Indapur Agro Feed", quantity: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.product) {
      toast.error("Please fill name, phone and product");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/enquiries/product", form);
      toast.success("Enquiry submitted! Our team will get back to you shortly.");
      setForm({ name: "", phone: "", email: "", product: "Indapur Agro Feed", quantity: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="product-enquiry-form">
      <div>
        <Label htmlFor="e-name" className="mb-1.5 block">Full Name *</Label>
        <Input id="e-name" data-testid="enquiry-name" value={form.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Your full name" />
      </div>
      <div>
        <Label htmlFor="e-phone" className="mb-1.5 block">Phone *</Label>
        <Input id="e-phone" data-testid="enquiry-phone" value={form.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="10-digit mobile" />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="e-email" className="mb-1.5 block">Email</Label>
        <Input id="e-email" type="email" data-testid="enquiry-email" value={form.email} onChange={(e) => onChange("email", e.target.value)} placeholder="you@example.com" />
      </div>
      <div>
        <Label className="mb-1.5 block">Product *</Label>
        <Select value={form.product} onValueChange={(v) => onChange("product", v)}>
          <SelectTrigger data-testid="enquiry-product">
            <SelectValue placeholder="Select product" />
          </SelectTrigger>
          <SelectContent>
            {PRODUCTS.map((p) => (
              <SelectItem key={p.key} value={p.name}>
                {p.name}
              </SelectItem>
            ))}
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="e-qty" className="mb-1.5 block">Quantity</Label>
        <Input id="e-qty" data-testid="enquiry-quantity" value={form.quantity} onChange={(e) => onChange("quantity", e.target.value)} placeholder="e.g. 5 bags / 100 kg" />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="e-msg" className="mb-1.5 block">Message</Label>
        <Textarea id="e-msg" data-testid="enquiry-message" rows={4} value={form.message} onChange={(e) => onChange("message", e.target.value)} placeholder="Tell us more about your requirement" />
      </div>
      <div className="sm:col-span-2">
        <Button type="submit" disabled={submitting} className="rounded-full bg-primary hover:bg-primary/90 w-full sm:w-auto px-8" data-testid="enquiry-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Send Enquiry
        </Button>
      </div>
    </form>
  );
}

function MessageForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We will get back to you soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="contact-message-form">
      <div>
        <Label htmlFor="c-name" className="mb-1.5 block">Name *</Label>
        <Input id="c-name" data-testid="contact-name" value={form.name} onChange={(e) => onChange("name", e.target.value)} placeholder="Your name" />
      </div>
      <div>
        <Label htmlFor="c-email" className="mb-1.5 block">Email *</Label>
        <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={(e) => onChange("email", e.target.value)} placeholder="you@example.com" />
      </div>
      <div>
        <Label htmlFor="c-phone" className="mb-1.5 block">Phone</Label>
        <Input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={(e) => onChange("phone", e.target.value)} placeholder="Mobile number" />
      </div>
      <div>
        <Label htmlFor="c-subject" className="mb-1.5 block">Subject</Label>
        <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={(e) => onChange("subject", e.target.value)} placeholder="Subject" />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="c-msg" className="mb-1.5 block">Message *</Label>
        <Textarea id="c-msg" data-testid="contact-message" rows={5} value={form.message} onChange={(e) => onChange("message", e.target.value)} placeholder="How can we help?" />
      </div>
      <div className="sm:col-span-2">
        <Button type="submit" disabled={submitting} className="rounded-full bg-primary hover:bg-primary/90 w-full sm:w-auto px-8" data-testid="contact-submit-btn">
          {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Send Message
        </Button>
      </div>
    </form>
  );
}

export default function ContactPage() {
  const [params] = useSearchParams();
  const initial = params.get("form") || "message";
  const [tab, setTab] = useState(initial);

  useEffect(() => {
    setTab(initial);
  }, [initial, setTab]);

  return (
    <div data-testid="contact-page">
      <section className="bg-primary text-white py-20 md:py-24 text-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs uppercase tracking-widest text-accent font-semibold">Contact</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mt-3 mb-4" style={{ fontFamily: "Outfit" }}>
            Get in touch
          </h1>
          <p className="text-white/80 max-w-2xl mx-auto">
            Register as a farmer, enquire about products or send us a message — we are here to help.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          {/* Contact info */}
          <div className="space-y-4 lg:col-span-1">
            {[
              { Icon: MapPin, title: "Address", value: SITE.address },
              { Icon: Phone, title: "Phone", value: SITE.phone, href: `tel:${SITE.phone.replace(/\s/g, "")}` },
              { Icon: Mail, title: "Email", value: SITE.email, href: `mailto:${SITE.email}` },
              { Icon: MessageCircle, title: "WhatsApp", value: `+91 ${SITE.whatsapp}`, href: SITE.whatsappLink },
            ].map(({ Icon, title, value, href }) => (
              <Card key={title} className="p-5 rounded-2xl border-border flex items-start gap-4" data-testid={`contact-info-${title.toLowerCase()}`}>
                <div className="w-11 h-11 rounded-xl bg-secondary text-primary flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{title}</div>
                  {href ? (
                    <a href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="text-sm font-medium hover:text-primary">
                      {value}
                    </a>
                  ) : (
                    <div className="text-sm font-medium">{value}</div>
                  )}
                </div>
              </Card>
            ))}
            <Card className="p-0 rounded-2xl border-border overflow-hidden">
              <iframe
                title="Indapur Map"
                src="https://www.google.com/maps?q=Indapur,%20Pune,%20Maharashtra&output=embed"
                className="w-full h-64 border-0"
                loading="lazy"
              />
            </Card>
          </div>

          {/* Forms */}
          <div className="lg:col-span-2">
            <Card className="p-6 md:p-8 rounded-2xl border-border">
              <Tabs value={tab} onValueChange={setTab} className="w-full">
                <TabsList className="grid grid-cols-3 mb-6" data-testid="contact-tabs">
                  <TabsTrigger value="message" data-testid="tab-message">Send Message</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Farmer Registration</TabsTrigger>
                  <TabsTrigger value="enquiry" data-testid="tab-enquiry">Product Enquiry</TabsTrigger>
                </TabsList>
                <TabsContent value="message">
                  <MessageForm />
                </TabsContent>
                <TabsContent value="register">
                  <FarmerForm />
                </TabsContent>
                <TabsContent value="enquiry">
                  <EnquiryForm />
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
